import { PingIcons, Row, Text } from "@/components";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import {
  FRESHNESS_STALE_AFTER_HOURS,
  FRESHNESS_WARN_AFTER_HOURS,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { MinimalCandidacy, PingType, PipelineStage } from "@/types";

export const LastPingDisplay = ({
  candidacy,
  stage,
  showTooltip = false,
}: {
  candidacy: MinimalCandidacy;
  stage: PipelineStage;
  showTooltip?: boolean;
}) => {
  const { userNameMap } = useTaggableUsers();

  /* Render */
  const render = () => {
    if (!candidacy.lastPing) return null;

    const lastPing = candidacy.lastPing;
    const at = lastPing.at;
    const hours = dates.calculateHoursAgo(at);
    const label = dates.timeAgoForPipelineCompact(at);
    const isNeutralStage = PipelineStage.APPLIED === stage;
    const isStale = !isNeutralStage && hours >= FRESHNESS_STALE_AFTER_HOURS;
    const isWarn =
      !isNeutralStage && !isStale && hours >= FRESHNESS_WARN_AFTER_HOURS;
    const stafferName = userNameMap[lastPing.stafferId] ?? "Unknown staffer";

    const colorClass = isStale
      ? "text-red-600"
      : isWarn
        ? "text-orange-600"
        : "text-slate-500";
    const textClass = cn(
      "text-[11px] tabular-nums",
      isStale || isWarn ? "font-medium" : "font-normal",
      colorClass,
    );

    const row = (
      <Row className="w-fit items-center gap-1.5">
        <PingIcons lastPing={lastPing} colorClass={colorClass} />
        <Text className={textClass}>{label}</Text>
      </Row>
    );

    if (!showTooltip) return row;

    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{row}</TooltipTrigger>
          <TooltipContent className="max-w-xs" side="bottom">
            <Text className="text-xs">{stafferName}</Text>
            <Text className="text-xs text-slate-400">
              {dates.formatExactDate(at)}
            </Text>
            {lastPing.type === PingType.MESSAGE && lastPing.body && (
              <Text className="mt-1 whitespace-pre-wrap text-xs italic">
                “{lastPing.body}”
              </Text>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return render();
};
